import { z } from 'zod/v4';
import { AssessmentDetailMetricResultSchema } from './assessment.js';

/**
 * `GET /risk-analysis/risk-profiles/:companyId/blueprints/:blueprintId/risk-scores`
 * params, ported from
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/risk-profile.js`'s
 * `blueprintRiskScores.params`.
 */
export const GetRiskScoresParamsSchema = z.object({
  companyId: z.string().min(1).max(36),
  blueprintId: z.string().min(1).max(36),
});

export type GetRiskScoresParams = z.infer<typeof GetRiskScoresParamsSchema>;

export const RiskGroupScoreSchema = z.object({
  riskGroup: z.string().nullable(),
  avgLikelihoodScore: z.number().nullable(),
  groupRiskFactorCount: z.number(),
});

/**
 * Response, ported from
 * `2025-06/risk-analysis-service/handlers/v3/assessments/get/index.js`'s
 * `getBlueprintRiskScores` over `data/assessments/get-risk-scores.js`, and the
 * `blueprintRiskScores` JSON-schema definition (`routes/schemas/v3/responses.js`)
 * that the legacy route served.
 *
 * `countryGrossLikelihoodScore`/`companyAverageNetLikelihoodScore` are nullable
 * here, unlike the legacy schema's non-nullable `type: 'number'` — the
 * underlying `net_score` column genuinely allows `NULL` (an assessment that
 * hasn't been scored yet), and the legacy declaration was never exercised in a
 * way any consumer depended on. Typing them as nullable reflects the real data
 * instead of repeating an untested contract lie, same reasoning as
 * `AssessmentDetailResourceSchema`'s `minPriorityScore`/`maxPriorityScore`.
 */
export const RiskScoresResourceSchema = z.object({
  assessmentId: z.string(),
  countryGrossLikelihoodScore: z.number().nullable(),
  companyAverageNetLikelihoodScore: z.number().nullable(),
  riskFactorsCount: z.number(),
  riskGroups: z.array(RiskGroupScoreSchema),
});

export type RiskScoresResource = z.infer<typeof RiskScoresResourceSchema>;

/**
 * `GET /risk-analysis/risk-profiles/:companyId/blueprints/:blueprintId/prioritization-details`
 * params, ported from `listPrioritizationDetails.params` in
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/risk-profile.js` —
 * the same pair as the risk-scores route.
 */
export const GetRiskProfilePrioritizationDetailsParamsSchema =
  GetRiskScoresParamsSchema;

export type GetRiskProfilePrioritizationDetailsParams = z.infer<
  typeof GetRiskProfilePrioritizationDetailsParamsSchema
>;

/**
 * Querystring, ported from `listPrioritizationDetails.querystring`. Page size
 * defaults to 15 — legacy AJV filled that in before the handler's own `20`
 * default could apply. `count` starts at 1 (legacy's `0` silently became 15).
 *
 * `sort` is `title` or `priorityScore`, optionally `:asc`/`:desc` in any case
 * — what legacy's `parseSortKey` understood. Anything else was an
 * `ORDER BY undefined` 500 there and is a 400 here. Unknown params are
 * stripped, not rejected, as AJV did: the old frontend's table sends a
 * `filter` this route never read.
 */
export const GetRiskProfilePrioritizationDetailsQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce.number().int().min(1).max(500).default(15),
  sort: z
    .string()
    .max(512)
    .regex(/^((title|priorityScore)(:(asc|desc))?)?$/i)
    .optional(),
});

export type GetRiskProfilePrioritizationDetailsQuery = z.infer<
  typeof GetRiskProfilePrioritizationDetailsQuerySchema
>;

/**
 * One risk of the caller-given company's latest READY assessment. Nulls come
 * out as legacy's serializer wrote them — `0` for a missing score, `[]` for no
 * metric results — since both frontends read `metricResults.find(...)`
 * unguarded.
 */
export const RiskProfilePrioritizationRowSchema = z.object({
  id: z.string(),
  title: z.string(),
  priorityScore: z.number(),
  minPriorityScore: z.number(),
  maxPriorityScore: z.number(),
  metricResults: z.array(AssessmentDetailMetricResultSchema),
});

export type RiskProfilePrioritizationRow = z.infer<
  typeof RiskProfilePrioritizationRowSchema
>;

/** The path of `GET /api/risk-analysis/risk-profiles/:companyId`. */
export const GetCompanyRiskProfileParamsSchema = z.object({
  companyId: z.string().min(1),
});

export type GetCompanyRiskProfileParams = z.infer<
  typeof GetCompanyRiskProfileParamsSchema
>;

/** One industry risk of a stored company risk profile. */
const CompanyRiskProfileRiskSchema = z.looseObject({
  riskId: z.string(),
  shortName: z.string(),
  category: z.string(),
  section: z.string(),
  subject: z.string(),
  institute: z.string(),
  definition: z.string(),
  grossScore: z.number().nullable(),
  netScore: z.number().nullable(),
  diffScore: z.number().nullable(),
  mitigations: z.array(z.unknown()),
});

/** One mitigation activity of a stored company risk profile. */
const CompanyRiskProfileMitigationSchema = z.looseObject({
  name: z.string(),
  type: z.string(),
  risks: z.array(z.unknown()),
  unusedProofs: z.array(z.unknown()).optional(),
});

/**
 * A company's industry risk profile as one mitigating company sees it — the
 * JSON stored in `industry_risk_company_profile_presentations.data`, plus the
 * row's `isRecalculatingProfile` flag (`0`/`1`). Loose objects keep every key
 * legacy sent: its response schema allowed any property, and the nested
 * entries carry more than the auditor page reads.
 */
export const CompanyRiskProfileSchema = z.looseObject({
  grossScore: z.number(),
  netScore: z.number(),
  environmentalScore: z.number(),
  socialScore: z.number(),
  governanceScore: z.number(),
  risks: z.array(CompanyRiskProfileRiskSchema),
  mitigations: z.array(CompanyRiskProfileMitigationSchema),
  isRecalculatingProfile: z.number().nullable(),
});

export type CompanyRiskProfile = z.infer<typeof CompanyRiskProfileSchema>;
